import React, { Suspense, useEffect } from "react";
import { createBrowserRouter, Link, Navigate, Outlet, useNavigate, useParams, useLocation, type RouteObject } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { cn } from "@/lib/utils/cn";
import { CARD_PADDED } from "../config/styles";
import { SPACING } from "../config/layout";
import { redirectWithSearch } from "../lib/navigation/workspace-routing";
import { ROUTES, contentLibraryItemPath, ragDocumentDetailPath } from "../lib/constants/routes";
import { formatDocumentTitle, getRouteConfig } from "../config/routes";
import { resolvePathChromeTitle } from "../lib/i18n/localize-navigation";
import { usePageHeaderMeta } from "../lib/workspace/page-header";
import { isChunkLoadError, lazyWithChunkRecovery, reloadOnceForChunkError } from "../lib/chunk-recovery";
import { useTranslation } from "../lib/hooks/use-translation";

import { ContentLoadingFallback, PageLoadingFallback } from "../components/ui/LoadingSpinner";
import AppLayout from "../components/layout/AppLayout";

const KnowledgeRails = lazyWithChunkRecovery(() => import("../pages/RAG/KnowledgeRails").then((m) => ({ default: m.KnowledgeRails })));
const PartiesRails = lazyWithChunkRecovery(() => import("../pages/Party/PartiesRails").then((m) => ({ default: m.PartiesRails })));
const GovernanceRails = lazyWithChunkRecovery(() => import("../pages/Governance/GovernanceRails").then((m) => ({ default: m.GovernanceRails })));
const DataGovernanceRails = lazyWithChunkRecovery(() => import("../pages/Governance/DataGovernanceRails").then((m) => ({ default: m.DataGovernanceRails })));
const IamRails = lazyWithChunkRecovery(() => import("../pages/IAM/IamRails").then((m) => ({ default: m.IamRails })));
const IntakeRails = lazyWithChunkRecovery(() => import("../pages/Intake/IntakeRails").then((m) => ({ default: m.IntakeRails })));
const WorkRails = lazyWithChunkRecovery(() => import("../pages/Work/WorkRails").then((m) => ({ default: m.WorkRails })));
const StrategyRails = lazyWithChunkRecovery(() => import("../pages/Strategy/StrategyRails").then((m) => ({ default: m.StrategyRails })));
const FinanceRails = lazyWithChunkRecovery(() => import("../pages/Finance/FinanceRails").then((m) => ({ default: m.FinanceRails })));
const ContractsRails = lazyWithChunkRecovery(() => import("../pages/Contracts/ContractsRails").then((m) => ({ default: m.ContractsRails })));
const ObservabilityRails = lazyWithChunkRecovery(() => import("../pages/Observability/ObservabilityRails").then((m) => ({ default: m.ObservabilityRails })));
const HrmRails = lazyWithChunkRecovery(() => import("../pages/Hrm/HrmRails").then((m) => ({ default: m.HrmRails })));

const DashboardPage = lazyWithChunkRecovery(() => import("../pages/Dashboard/DashboardPage"));
const AIPage = lazyWithChunkRecovery(() => import("../pages/AI/AIPage"));
const AIChatPage = lazyWithChunkRecovery(() => import("../pages/AI/AIChatPage"));
const AIModelsPage = lazyWithChunkRecovery(() => import("../pages/AI/AIModelsPage"));
const AIGenerationPage = lazyWithChunkRecovery(() => import("../pages/AI/AIGenerationPage"));
const CodeGeneratorPage = lazyWithChunkRecovery(() => import("../pages/AI/CodeGeneratorPage"));
const CodeAnalysisPage = lazyWithChunkRecovery(() => import("../pages/AI/CodeAnalysisPage"));
const AIConversationPage = lazyWithChunkRecovery(() => import("../pages/AI/AIConversationPage"));
const AIAnalysisPage = lazyWithChunkRecovery(() => import("../pages/AI/AIAnalysisPage"));
const NiceTravelAgentPage = lazyWithChunkRecovery(() => import("../pages/AI/NiceTravelAgentPage"));
const CorpusPage = lazyWithChunkRecovery(() => import("../pages/RAG/CorpusPage"));
const PreRagReviewPage = lazyWithChunkRecovery(() => import("../pages/RAG/PreRagReviewPage"));
const PreRagReviewDetailPage = lazyWithChunkRecovery(() => import("../pages/RAG/PreRagReviewDetailPage"));
const RAGDocumentDetailPage = lazyWithChunkRecovery(() => import("../pages/RAG/documents/DetailPage"));
const RAGSearchPage = lazyWithChunkRecovery(() => import("../pages/RAG/SearchLabPage"));
const EmbeddingsPage = lazyWithChunkRecovery(() => import("../pages/RAG/EmbeddingsPage"));
const IndexOpsPage = lazyWithChunkRecovery(() => import("../pages/RAG/IndexOpsPage"));
const RAGEvaluationPage = lazyWithChunkRecovery(() => import("../pages/RAG/EvaluationPage"));
const IngestPage = lazyWithChunkRecovery(() => import("../pages/RAG/IngestPage"));
const RankingPage = lazyWithChunkRecovery(() => import("../pages/Ranking/RankingPage"));
const AnalyticsPage = lazyWithChunkRecovery(() => import("../pages/Analytics/AnalyticsPage"));
const DatabasePage = lazyWithChunkRecovery(() => import("../pages/Database/DatabasePage"));
const DatabaseManagementPage = lazyWithChunkRecovery(() => import("../pages/Database/DatabaseManagementPage"));
const MonitoringPage = lazyWithChunkRecovery(() => import("../pages/Monitoring/MonitoringPage"));
const ServicesPage = lazyWithChunkRecovery(() => import("../pages/Services"));
const DeploymentPage = lazyWithChunkRecovery(() => import("../pages/Deployment/DeploymentPage"));
const PerformancePage = lazyWithChunkRecovery(() => import("../pages/Performance/PerformancePage"));
const EmbedChatPage = lazyWithChunkRecovery(() => import("../pages/Embed/EmbedChatPage"));
const WorkspaceLayout = lazyWithChunkRecovery(() => import("../components/workspace/Layout"));
const PlatformSearchPage = lazyWithChunkRecovery(() => import("../pages/Platform/SearchPage"));
const IntakeDashboardPage = lazyWithChunkRecovery(() => import("../components/intake/pages/DashboardPage"));
const IntakeDraftsPage = lazyWithChunkRecovery(() => import("../components/intake/pages/DraftsPage"));
const ConsultationPage = lazyWithChunkRecovery(() => import("../pages/Conversation/ConsultationPage"));
const WorkScopePage = lazyWithChunkRecovery(() => import("../pages/Conversation/WorkScopePage"));
const IntakeLedgerPage = lazyWithChunkRecovery(() => import("../components/intake/pages/LedgerPage"));
const IntakeLedgerEntryPage = lazyWithChunkRecovery(() => import("../components/intake/pages/LedgerEntryPage"));
const IntakeOperationsDigestPage = lazyWithChunkRecovery(() => import("../components/intake/pages/OperationsDigestPage"));
const WorkProjectsPage = lazyWithChunkRecovery(() => import("../pages/Work/ProjectsPage"));
const WorkTasksPage = lazyWithChunkRecovery(() => import("../pages/Work/TasksPage"));
const WorkAnalyticsPage = lazyWithChunkRecovery(() => import("../pages/Work/AnalyticsPage"));
const StrategyPage = lazyWithChunkRecovery(() => import("../pages/Strategy/StrategyPage"));
const UserWorkspaceSettingsPage = lazyWithChunkRecovery(() => import("../pages/UserWorkspace/SettingsPage"));
const GovernancePromptTemplatesPage = lazyWithChunkRecovery(() => import("../pages/Governance/PromptTemplatesPage"));
const GovernanceAdministrationPage = lazyWithChunkRecovery(() => import("../pages/Governance/AdministrationPage"));
const GovernanceAiAuditPage = lazyWithChunkRecovery(() => import("../pages/Governance/AiAuditPage"));
const GovernanceAiAuditEntryPage = lazyWithChunkRecovery(() => import("../pages/Governance/AiAuditEntryPage"));
const ContractsAgreementsPage = lazyWithChunkRecovery(() => import("../pages/Contracts/AgreementsPage"));
const ContractsTemplatesPage = lazyWithChunkRecovery(() => import("../pages/Contracts/TemplatesPage"));
const FinanceInvoicesPage = lazyWithChunkRecovery(() => import("../pages/Finance/InvoicesPage"));
const FinancePayablesPage = lazyWithChunkRecovery(() => import("../pages/Finance/PayablesPage"));
const ContentReviewPage = lazyWithChunkRecovery(() => import("../pages/Content/ContentReviewPage"));
const ContentCapturePage = lazyWithChunkRecovery(() => import("../pages/Content/ContentCapturePage"));
const ContentOverviewPage = lazyWithChunkRecovery(() => import("../pages/Content/ContentOverviewPage"));
const ContentLibraryPage = lazyWithChunkRecovery(() => import("../pages/Content/ContentLibraryPage"));
const ContentItemDetailPage = lazyWithChunkRecovery(() => import("../pages/Content/ContentItemDetailPage"));
const ContentStoragePage = lazyWithChunkRecovery(() => import("../pages/Content/ContentStoragePage"));
const FinancePaymentDossiersPage = lazyWithChunkRecovery(() => import("../pages/Finance/PaymentDossiersPage"));
const HrmCatalogPage = lazyWithChunkRecovery(() => import("../pages/Hrm/CatalogPage"));
const FinanceReconciliationPage = lazyWithChunkRecovery(() => import("../pages/Finance/ReconciliationPage"));
const FinanceBankPage = lazyWithChunkRecovery(() => import("../pages/Finance/BankPage"));
const FinanceReportingPage = lazyWithChunkRecovery(() => import("../pages/Finance/ReportingPage"));
const IntakeIndexRedirect = lazyWithChunkRecovery(() => import("../components/intake/pages/IndexRedirect"));
const IntakeRecordsPage = lazyWithChunkRecovery(() => import("../components/intake/pages/RecordsPage"));
const IntakeRecordDetailPage = lazyWithChunkRecovery(() => import("../components/intake/pages/RecordDetailPage"));
const PartiesPage = lazyWithChunkRecovery(() => import("../pages/Party/PartiesPage"));
const WorkCustomViewPage = lazyWithChunkRecovery(() => import("../pages/Work/CustomViewPage"));
const GovernanceLlmPrivacyPage = lazyWithChunkRecovery(() => import("../pages/Governance/LlmPrivacyPage"));
const GovernanceOperationalPage = lazyWithChunkRecovery(() => import("../pages/Governance/OperationalPage"));
const GovernanceIntegrationsPage = lazyWithChunkRecovery(() => import("../pages/Governance/IntegrationsPage"));
const GovernanceSyncDiagnosticsPage = lazyWithChunkRecovery(() => import("../pages/Governance/SyncDiagnosticsPage"));
const CatalogPage = lazyWithChunkRecovery(() => import("../pages/Governance/data-governance/CatalogPage"));
const PolicyPage = lazyWithChunkRecovery(() => import("../pages/Governance/data-governance/PolicyPage"));
const DriftPage = lazyWithChunkRecovery(() => import("../pages/Governance/data-governance/DriftPage"));
const ProjectionPage = lazyWithChunkRecovery(() => import("../pages/Governance/data-governance/ProjectionPage"));
const IamAuthorizationPage = lazyWithChunkRecovery(() => import("../pages/IAM/AuthorizationPage"));
const IamRolesPage = lazyWithChunkRecovery(() => import("../pages/IAM/roles/RolesPage"));
const IamRoleDetailPage = lazyWithChunkRecovery(() => import("../pages/IAM/roles/DetailPage"));
const WorkflowAiPolicyPage = lazyWithChunkRecovery(() => import("../pages/AI/WorkflowAiPolicyPage"));
const GovernanceRulesPage = lazyWithChunkRecovery(() => import("../pages/Governance/RulesPage"));
const GovernanceTemplateRoutingRulesPage = lazyWithChunkRecovery(() => import("../pages/Governance/TemplateRoutingRulesPage"));
const IntakeDraftEntryPage = lazyWithChunkRecovery(() => import("../components/intake/pages/DraftEntryPage"));
const DocsHubPage = lazyWithChunkRecovery(() => import("../components/intake/pages/DocsHubPage"));
const UserGuidePage = lazyWithChunkRecovery(() => import("../components/intake/pages/UserGuidePage"));
const TechSpecPage = lazyWithChunkRecovery(() => import("../components/intake/pages/TechSpecPage"));
const AdminChecklistPage = lazyWithChunkRecovery(() => import("../components/intake/pages/AdminChecklistPage"));
const LoginPage = lazyWithChunkRecovery(() => import("../pages/Auth/LoginPage"));
const CallbackPage = lazyWithChunkRecovery(() => import("../pages/Auth/CallbackPage"));
const FeaturesPage = lazyWithChunkRecovery(() => import("../pages/Commercial/FeaturesPage"));
const PricingPage = lazyWithChunkRecovery(() => import("../pages/Commercial/PricingPage"));
const EconomicsPage = lazyWithChunkRecovery(() => import("../pages/Commercial/EconomicsPage"));

const PageLoading: React.FC = () => <PageLoadingFallback />;

const ContentLoading: React.FC = () => <ContentLoadingFallback />;

const workspacePage = (element: React.ReactNode) => <Suspense fallback={<ContentLoading />}>{element}</Suspense>;

const RagDocumentLegacyRedirect: React.FC = () => {
  const { documentId } = useParams();
  if (!documentId) {
    return <Navigate to={ROUTES.KNOWLEDGE_CORPUS} replace />;
  }
  return <Navigate to={ragDocumentDetailPath(documentId)} replace />;
};

const ContentLibraryItemLegacyRedirect: React.FC = () => {
  const { itemId } = useParams();
  if (!itemId) {
    return <Navigate to={ROUTES.KNOWLEDGE_CONTENT_LIBRARY} replace />;
  }
  return <Navigate to={contentLibraryItemPath(itemId)} replace />;
};

const NotFoundPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <section
      className="relative flex min-h-0 flex-1 flex-col items-center justify-center overflow-hidden px-6 py-12"
      data-testid="not-found"
      role="status"
      aria-live="polite"
      aria-labelledby="not-found-title"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgb(var(--muted-rgb)/0.55)_0%,transparent_62%)]"
      />
      <div className="relative flex w-full max-w-[22rem] flex-col items-center text-center">
        <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground/75">404</p>
        <h1 id="not-found-title" className="mt-2 text-xl font-semibold tracking-tight text-foreground">
          {t("notFound.title")}
        </h1>
        <div className="mt-7 flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            className={cn(
              "inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-background px-3.5 text-sm font-medium text-foreground hover:bg-muted/50 focus:outline-none focus:ring-2 focus:ring-ring",
            )}
            onClick={() => navigate(-1)}
          >
            {t("notFound.goBack")}
          </button>
          <Link
            to="/"
            className={cn(
              "inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3.5 text-sm font-medium text-primary-foreground hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-ring",
            )}
          >
            {t("notFound.goHome")}
          </Link>
        </div>
      </div>
    </section>
  );
};

const ErrorFallback: React.FC<{
  error: Error;
  resetErrorBoundary: () => void;
}> = ({ error, resetErrorBoundary }) => {
  const { t } = useTranslation();
  const chunkError = isChunkLoadError(error);

  useEffect(() => {
    if (!chunkError) {
      return;
    }
    reloadOnceForChunkError("ErrorFallback", error);
  }, [chunkError, error]);

  const title = chunkError ? t("common.chunkUpdateTitle") : t("common.somethingWentWrong");
  const description = chunkError ? t("common.chunkUpdateMessage") : t("common.pageLoadError");
  const actionLabel = chunkError ? t("common.reloadApp") : t("common.retry");

  return (
    <div className={cn("flex min-h-screen flex-col items-center justify-center bg-background", SPACING.CARD_PADDING)}>
      <div className={cn("w-full max-w-md", CARD_PADDED)}>
        <div className={cn("flex items-center", SPACING.MARGIN_BOTTOM)}>
          <svg className="h-5 w-5 text-destructive" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
          <p className="ml-3 text-sm font-medium text-foreground">{title}</p>
        </div>
        <div className={cn("text-sm text-muted-foreground", SPACING.MARGIN_BOTTOM)}>
          <p>{description}</p>
          <p className={cn("mt-1 font-mono text-xs rounded bg-muted text-foreground", SPACING.CARD_PADDING)}>{error.message}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            if (chunkError) {
              window.location.reload();
              return;
            }
            resetErrorBoundary();
          }}
          className={cn("w-full rounded-md bg-primary text-primary-foreground hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-ring", SPACING.CARD_PADDING)}
        >
          {actionLabel}
        </button>
      </div>
    </div>
  );
};

/** Reset sticky error UI when the route changes (nav no longer leaves a dead page). */
const RouteErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} resetKeys={[location.key, location.pathname]}>
      {children}
    </ErrorBoundary>
  );
};

const WorkspaceOutletLayout: React.FC = () => (
  <WorkspaceLayout>
    <Suspense fallback={<ContentLoadingFallback />}>
      <Outlet />
    </Suspense>
  </WorkspaceLayout>
);

const IntakeOutletLayout: React.FC = () => (
  <WorkspaceLayout>
    <IntakeRails />
    <Suspense fallback={<ContentLoadingFallback />}>
      <Outlet />
    </Suspense>
  </WorkspaceLayout>
);

const WorkOutletLayout: React.FC = () => (
  <WorkspaceLayout>
    <WorkRails />
    <Suspense fallback={<ContentLoadingFallback />}>
      <Outlet />
    </Suspense>
  </WorkspaceLayout>
);

const StrategyOutletLayout: React.FC = () => (
  <WorkspaceLayout>
    <StrategyRails />
    <Suspense fallback={<ContentLoadingFallback />}>
      <Outlet />
    </Suspense>
  </WorkspaceLayout>
);

const FinanceOutletLayout: React.FC = () => (
  <WorkspaceLayout>
    <FinanceRails />
    <Suspense fallback={<ContentLoadingFallback />}>
      <Outlet />
    </Suspense>
  </WorkspaceLayout>
);

const HrmOutletLayout: React.FC = () => (
  <WorkspaceLayout>
    <HrmRails>
      <Suspense fallback={<ContentLoadingFallback />}>
        <Outlet />
      </Suspense>
    </HrmRails>
  </WorkspaceLayout>
);

const ContractsOutletLayout: React.FC = () => (
  <WorkspaceLayout>
    <ContractsRails />
    <Suspense fallback={<ContentLoadingFallback />}>
      <Outlet />
    </Suspense>
  </WorkspaceLayout>
);

const ObservabilityOutletLayout: React.FC = () => (
  <WorkspaceLayout>
    <ObservabilityRails />
    <Suspense fallback={<ContentLoadingFallback />}>
      <Outlet />
    </Suspense>
  </WorkspaceLayout>
);

const KnowledgeOutletLayout: React.FC = () => (
  <WorkspaceLayout>
    <KnowledgeRails />
    <Suspense fallback={<ContentLoadingFallback />}>
      <Outlet />
    </Suspense>
  </WorkspaceLayout>
);

const PartiesOutletLayout: React.FC = () => (
  <WorkspaceLayout>
    <PartiesRails />
    <Suspense fallback={<ContentLoadingFallback />}>
      <Outlet />
    </Suspense>
  </WorkspaceLayout>
);

const GovernanceOutletLayout: React.FC = () => (
  <WorkspaceLayout>
    <GovernanceRails />
    <Suspense fallback={<ContentLoadingFallback />}>
      <Outlet />
    </Suspense>
  </WorkspaceLayout>
);

const DataGovernanceOutletLayout: React.FC = () => (
  <WorkspaceLayout>
    <DataGovernanceRails />
    <Suspense fallback={<ContentLoadingFallback />}>
      <Outlet />
    </Suspense>
  </WorkspaceLayout>
);

const IamOutletLayout: React.FC = () => (
  <WorkspaceLayout>
    <IamRails />
    <Suspense fallback={<ContentLoadingFallback />}>
      <Outlet />
    </Suspense>
  </WorkspaceLayout>
);

const WorkProjectDetailRoute: React.FC = () => {
  const { projectId } = useParams();
  return <WorkProjectsPage detailOnly detailProjectId={projectId ?? null} />;
};

const WorkProjectCreateRoute: React.FC = () => <WorkProjectsPage detailOnly startInCreateMode />;

const PartyIdentityReviewRoute: React.FC = () => <Navigate to={ROUTES.PARTIES} replace />;

const PartyDetailRoute: React.FC<{ partyRole?: "customer" | "supplier" | "partner" }> = ({ partyRole }) => {
  const { partyId } = useParams();
  return <PartiesPage detailOnly detailPartyId={partyId ?? null} partyRole={partyRole} />;
};

const ContractsAgreementDetailRoute: React.FC = () => {
  const { agreementId } = useParams();
  return <ContractsAgreementsPage detailOnly detailAgreementId={agreementId ?? null} />;
};

const ContractsTemplateDetailRoute: React.FC = () => {
  const { templateUuid } = useParams();
  return <ContractsTemplatesPage detailOnly detailTemplateUuid={templateUuid ?? null} />;
};

const FinanceInvoiceDetailRoute: React.FC = () => {
  const { invoiceId } = useParams();
  return <FinanceInvoicesPage detailOnly detailInvoiceId={invoiceId ?? null} />;
};

const FinancePayableDetailRoute: React.FC = () => {
  const { payableId } = useParams();
  return <FinancePayablesPage detailOnly detailPayableId={payableId ?? null} />;
};

const FinancePaymentDossierDetailRoute: React.FC = () => {
  const { dossierTypeId } = useParams();
  return <FinancePaymentDossiersPage detailOnly detailDossierTypeId={dossierTypeId ?? null} />;
};

const FinanceBankRoute: React.FC = () => <FinanceBankPage />;

const FinanceBankTransactionsRoute: React.FC = () => <FinanceBankPage ledgerOnly />;

const FinanceBankAccountDetailRoute: React.FC = () => {
  const { bankAccountId } = useParams();
  return <FinanceBankPage detailBankAccountId={bankAccountId ?? null} />;
};

const FinanceReportingRoute: React.FC = () => <FinanceReportingPage />;

const WorkTaskDetailRoute: React.FC = () => {
  const { taskId } = useParams();
  return <WorkTasksPage detailOnly detailTaskId={taskId ?? null} />;
};

const UserWorkspaceProfilePage: React.FC = () => <UserWorkspaceSettingsPage section="profile" />;
const UserWorkspaceMailPage: React.FC = () => <UserWorkspaceSettingsPage section="mail" />;
const UserWorkspaceAiDefaultsPage: React.FC = () => <UserWorkspaceSettingsPage section="ai-defaults" />;
const UserWorkspaceZaloPage: React.FC = () => <UserWorkspaceSettingsPage section="zalo" />;
const GovernanceTriageTemplatesRoute: React.FC = () => <Navigate to={`${ROUTES.GOVERNANCE_DRAFT_TEMPLATES}?kind=triage`} replace />;
const GovernanceThreadDigestTemplatesRoute: React.FC = () => <Navigate to={`${ROUTES.GOVERNANCE_DRAFT_TEMPLATES}?kind=thread_digest`} replace />;
const GovernanceRequestInfoTemplatesRoute: React.FC = () => <Navigate to={`${ROUTES.GOVERNANCE_DRAFT_TEMPLATES}?kind=request_info`} replace />;
const IamUsersPage: React.FC = () => <IamAuthorizationPage initialWorkspaceView="users" />;
const IamUserDetailPage: React.FC = () => {
  const { userId } = useParams();
  return <IamAuthorizationPage initialWorkspaceView="users" detailUserId={userId ?? ""} />;
};
const IamTenantsPage: React.FC = () => <IamAuthorizationPage initialWorkspaceView="tenants" />;
const IamTenantDetailPage: React.FC = () => {
  const { tenantId } = useParams();
  return <IamAuthorizationPage initialWorkspaceView="tenants" detailTenantId={tenantId ?? ""} />;
};
const IamServicesPage: React.FC = () => <IamAuthorizationPage initialWorkspaceView="services" />;
const IamServiceItemsPage: React.FC = () => <IamAuthorizationPage initialWorkspaceView="service-items" />;
const IamSyncPage: React.FC = () => <IamAuthorizationPage initialWorkspaceView="sync" />;
const IamAuditPage: React.FC = () => <IamAuthorizationPage initialWorkspaceView="audit" />;
const IamSimulatorPage: React.FC = () => <IamAuthorizationPage initialWorkspaceView="simulator" />;

const LegacyRouteRedirect: React.FC<{ to: string }> = ({ to }) => {
  const location = useLocation();
  return <Navigate to={redirectWithSearch(to, location.search)} replace />;
};

const LegacyRouteParamRedirect: React.FC<{ base: string; paramKey: string }> = ({ base, paramKey }) => {
  const params = useParams();
  const location = useLocation();
  const value = params[paramKey];
  const pathname = value ? `${base}/${encodeURIComponent(value)}` : base;
  return <Navigate to={redirectWithSearch(pathname, location.search)} replace />;
};

const CustomersLegacyRedirect: React.FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const to = params.get("mode") === "identity_review" ? ROUTES.PARTY_IDENTITY_REVIEW : ROUTES.PARTIES;
  if (params.get("mode") === "identity_review") {
    params.delete("mode");
  }
  const rest = params.toString();
  return <Navigate to={rest ? `${to}?${rest}` : to} replace />;
};

const legacyIntakeRouteChildren: RouteObject[] = [
  { index: true, element: workspacePage(<IntakeIndexRedirect />) },
  { path: "dashboard", element: <LegacyRouteRedirect to={ROUTES.DASHBOARD} /> },
  { path: "drafts", element: <LegacyRouteRedirect to={ROUTES.INTAKE_DRAFTS} /> },
  { path: "drafts/:recordId", element: <LegacyRouteParamRedirect base={ROUTES.INTAKE_DRAFTS} paramKey="recordId" /> },
  { path: "digest", element: <LegacyRouteRedirect to={ROUTES.INTAKE_DIGEST} /> },
  { path: "records", element: <LegacyRouteRedirect to={ROUTES.INTAKE_RECORDS} /> },
  { path: "records/:recordId", element: <LegacyRouteParamRedirect base={ROUTES.INTAKE_RECORDS} paramKey="recordId" /> },
  { path: "consultation", element: <LegacyRouteRedirect to={ROUTES.CONVERSATIONS} /> },
  { path: "consultation/work-scope", element: <LegacyRouteRedirect to={ROUTES.CONVERSATIONS_CHANNEL_SCOPE} /> },
  { path: "ledger", element: <LegacyRouteRedirect to={ROUTES.INTAKE_LEDGER} /> },
  { path: "notification", element: <LegacyRouteRedirect to={ROUTES.INTAKE_DIGEST} /> },
  { path: "projects", element: <LegacyRouteRedirect to={ROUTES.WORK_PROJECTS} /> },
  { path: "projects/:projectId", element: <LegacyRouteParamRedirect base={ROUTES.WORK_PROJECTS} paramKey="projectId" /> },
  { path: "hub-data", element: <Navigate to={ROUTES.PARTIES} replace /> },
  { path: "tasks", element: <LegacyRouteRedirect to={ROUTES.WORK_TASKS} /> },
  { path: "tasks/:taskId", element: <LegacyRouteParamRedirect base={ROUTES.WORK_TASKS} paramKey="taskId" /> },
  { path: "custom-view", element: <LegacyRouteRedirect to={ROUTES.WORK_CUSTOM_VIEW} /> },
  { path: "analytics", element: <LegacyRouteRedirect to={ROUTES.WORK_ANALYTICS} /> },
  { path: "settings", element: <LegacyRouteRedirect to={ROUTES.USER_ACCOUNT_SETTINGS} /> },
  { path: "settings/me", element: <Navigate to={ROUTES.USER_ACCOUNT_SETTINGS} replace /> },
  { path: "settings/me/profile", element: <LegacyRouteRedirect to={ROUTES.USER_ACCOUNT_SETTINGS} /> },
  { path: "settings/me/mail", element: <LegacyRouteRedirect to={ROUTES.USER_ACCOUNT_SETTINGS_MAIL} /> },
  { path: "settings/me/ai-defaults", element: <LegacyRouteRedirect to={ROUTES.USER_ACCOUNT_SETTINGS_AI_DEFAULTS} /> },
  { path: "settings/me/zalo", element: <LegacyRouteRedirect to={ROUTES.USER_ACCOUNT_SETTINGS_ZALO} /> },
  { path: "settings/me/password", element: <Navigate to={ROUTES.USER_ACCOUNT_SETTINGS} replace /> },
  { path: "settings/me/mail/oauth-close", element: <LegacyRouteRedirect to={ROUTES.USER_ACCOUNT_SETTINGS_MAIL_OAUTH_CLOSE} /> },
  { path: "prompt-templates", element: <LegacyRouteRedirect to={ROUTES.GOVERNANCE_DRAFT_TEMPLATES} /> },
  { path: "triage-templates", element: <LegacyRouteRedirect to={ROUTES.GOVERNANCE_TRIAGE_TEMPLATES} /> },
  { path: "thread-digest-templates", element: <LegacyRouteRedirect to={ROUTES.GOVERNANCE_THREAD_DIGEST_TEMPLATES} /> },
  { path: "request-info-templates", element: <LegacyRouteRedirect to={ROUTES.GOVERNANCE_REQUEST_INFO_TEMPLATES} /> },
  { path: "rules", element: <LegacyRouteRedirect to={ROUTES.GOVERNANCE_RULES} /> },
  { path: "rules/llm-privacy", element: <LegacyRouteRedirect to={ROUTES.GOVERNANCE_AI_PRIVACY_RULES} /> },
  { path: "rules/template-routing-rules", element: <LegacyRouteRedirect to={ROUTES.GOVERNANCE_TEMPLATE_ROUTING_RULES} /> },
  { path: "ai-audit", element: <LegacyRouteRedirect to={ROUTES.GOVERNANCE_AI_AUDIT} /> },
  { path: "ai-audit/:auditId", element: <LegacyRouteParamRedirect base={ROUTES.GOVERNANCE_AI_AUDIT} paramKey="auditId" /> },
  { path: "llm-privacy", element: <LegacyRouteRedirect to={ROUTES.GOVERNANCE_AI_PRIVACY_RULES} /> },
  { path: "admin", element: <LegacyRouteRedirect to={ROUTES.GOVERNANCE_ADMINISTRATION} /> },
  { path: "admin/operational", element: <LegacyRouteRedirect to={ROUTES.GOVERNANCE_OPERATIONAL_SETTINGS} /> },
  { path: "admin/integrations", element: <LegacyRouteRedirect to={ROUTES.GOVERNANCE_INTEGRATIONS} /> },
  { path: "admin/sync-diagnostics", element: <LegacyRouteRedirect to={ROUTES.GOVERNANCE_SYNC_DIAGNOSTICS} /> },
  { path: "admin/authz", element: <Navigate to={ROUTES.ADMIN_AUTHZ_ROLES} replace /> },
  { path: "admin/authz/roles", element: <Navigate to={ROUTES.ADMIN_AUTHZ_ROLES} replace /> },
  { path: "admin/authz/users", element: <Navigate to={ROUTES.ADMIN_AUTHZ_USERS} replace /> },
  { path: "admin/llm-runtime", element: <Navigate to={ROUTES.AI_WORKFLOW_POLICY} replace /> },
  { path: "admin/rules", element: <LegacyRouteRedirect to={ROUTES.ADMIN_RULES} /> },
  { path: "admin/template-routing-rules", element: <LegacyRouteRedirect to={ROUTES.ADMIN_TEMPLATE_ROUTING_RULES} /> },
  { path: "admin/ledger", element: <LegacyRouteRedirect to={ROUTES.ADMIN_LEDGER} /> },
  { path: "admin/ledger/:entryId", element: <LegacyRouteParamRedirect base={ROUTES.ADMIN_LEDGER} paramKey="entryId" /> },
  { path: "tasks/new", element: <Navigate to={`${ROUTES.WORK_TASKS}?mode=create`} replace /> },
  { path: "projects/new", element: <Navigate to={`${ROUTES.WORK_PROJECTS}?mode=create`} replace /> },
];

const DocumentTitleSync: React.FC = () => {
  const location = useLocation();
  const headerMeta = usePageHeaderMeta();
  const { t } = useTranslation();
  useEffect(() => {
    const registeredTitle = headerMeta?.title;
    if (typeof registeredTitle === "string" && registeredTitle.trim().length > 0) {
      document.title = formatDocumentTitle(registeredTitle.trim());
      return;
    }
    const routeTitle = getRouteConfig(location.pathname).title;
    document.title = formatDocumentTitle(resolvePathChromeTitle(location.pathname, t, routeTitle));
  }, [headerMeta, location.pathname, t]);
  return null;
};

const withDocumentTitle = (element: React.ReactNode) => (
  <>
    <DocumentTitleSync />
    {element}
  </>
);

const appRoutes: RouteObject[] = [
  {
    path: "/login",
    element: withDocumentTitle(
      <RouteErrorBoundary>
        <Suspense fallback={<PageLoading />}>
          <LoginPage />
        </Suspense>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/callback",
    element: withDocumentTitle(
      <RouteErrorBoundary>
        <Suspense fallback={<PageLoading />}>
          <CallbackPage />
        </Suspense>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/features",
    element: withDocumentTitle(
      <RouteErrorBoundary>
        <Suspense fallback={<PageLoading />}>
          <FeaturesPage />
        </Suspense>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/pricing",
    element: withDocumentTitle(
      <RouteErrorBoundary>
        <Suspense fallback={<PageLoading />}>
          <PricingPage />
        </Suspense>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/economics",
    element: withDocumentTitle(
      <RouteErrorBoundary>
        <Suspense fallback={<PageLoading />}>
          <EconomicsPage />
        </Suspense>
      </RouteErrorBoundary>
    ),
  },
  {
    path: "/",
    element: withDocumentTitle(
      <RouteErrorBoundary>
        <AppLayout />
      </RouteErrorBoundary>
    ),
    errorElement: <ErrorFallback error={new Error("Layout error")} resetErrorBoundary={() => window.location.reload()} />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageLoading />}>
            <DashboardPage />
          </Suspense>
        ),
      },
      {
        path: "ai",
        element: (
          <Suspense fallback={<PageLoading />}>
            <AIPage />
          </Suspense>
        ),
        children: [
          { index: true, element: <Navigate to="/ai/chat" replace /> },
          {
            path: "nice-travel",
            element: (
              <Suspense fallback={<PageLoading />}>
                <NiceTravelAgentPage />
              </Suspense>
            ),
          },
          {
            path: "chat",
            element: (
              <Suspense fallback={<PageLoading />}>
                <AIChatPage />
              </Suspense>
            ),
          },
          {
            path: "models",
            element: (
              <Suspense fallback={<PageLoading />}>
                <AIModelsPage />
              </Suspense>
            ),
          },
          {
            path: "runtime",
            element: workspacePage(<WorkflowAiPolicyPage />),
          },
          {
            path: "generate",
            element: (
              <Suspense fallback={<PageLoading />}>
                <AIGenerationPage />
              </Suspense>
            ),
          },
          {
            path: "upload",
            element: <Navigate to={ROUTES.KNOWLEDGE_CORPUS} replace />,
          },
          {
            path: "conversation",
            element: (
              <Suspense fallback={<PageLoading />}>
                <AIConversationPage />
              </Suspense>
            ),
          },
          {
            path: "analysis",
            element: (
              <Suspense fallback={<PageLoading />}>
                <AIAnalysisPage />
              </Suspense>
            ),
          },
          {
            path: "code-generator",
            element: (
              <Suspense fallback={<PageLoading />}>
                <CodeGeneratorPage />
              </Suspense>
            ),
          },
          {
            path: "code-analysis",
            element: (
              <Suspense fallback={<PageLoading />}>
                <CodeAnalysisPage />
              </Suspense>
            ),
          },
          {
            path: "ranking",
            element: workspacePage(<RankingPage />),
          },
        ],
      },
      {
        path: "knowledge",
        element: workspacePage(<KnowledgeOutletLayout />),
        children: [
          { index: true, element: <Navigate to={ROUTES.KNOWLEDGE_CORPUS} replace /> },
          { path: "corpus", element: workspacePage(<CorpusPage />) },
          { path: "corpus/:documentId", element: workspacePage(<RAGDocumentDetailPage />) },
          { path: "review", element: workspacePage(<PreRagReviewPage />) },
          { path: "review/:rowId", element: workspacePage(<PreRagReviewDetailPage />) },
          { path: "ingest", element: workspacePage(<IngestPage />) },
          { path: "content", element: workspacePage(<ContentOverviewPage />) },
          { path: "content/capture", element: workspacePage(<ContentCapturePage />) },
          { path: "content/review", element: workspacePage(<ContentReviewPage />) },
          { path: "content/library", element: workspacePage(<ContentLibraryPage />) },
          { path: "content/library/:itemId", element: workspacePage(<ContentItemDetailPage />) },
          { path: "content/storage", element: workspacePage(<ContentStoragePage />) },
          { path: "ops", element: workspacePage(<IndexOpsPage />) },
          { path: "projects", element: <Navigate to={ROUTES.KNOWLEDGE_CORPUS} replace /> },
          { path: "embeddings", element: workspacePage(<EmbeddingsPage />) },
          { path: "search-lab", element: workspacePage(<RAGSearchPage />) },
          { path: "evaluation", element: workspacePage(<RAGEvaluationPage />) },
        ],
      },
      {
        path: "rag",
        children: [
          { index: true, element: <Navigate to={ROUTES.KNOWLEDGE_CORPUS} replace /> },
          { path: "evaluation", element: <Navigate to={`${ROUTES.KNOWLEDGE_PLATFORM}/evaluation`} replace /> },
          { path: "documents", element: <Navigate to={ROUTES.KNOWLEDGE_CORPUS} replace /> },
          { path: "documents/:documentId", element: <RagDocumentLegacyRedirect /> },
          { path: "projects", element: <Navigate to={ROUTES.KNOWLEDGE_CORPUS} replace /> },
          { path: "search", element: <Navigate to={ROUTES.KNOWLEDGE_SEARCH_LAB} replace /> },
          { path: "chat", element: <Navigate to="/ai/chat" replace /> },
          { path: "embeddings", element: <Navigate to={ROUTES.KNOWLEDGE_EMBEDDINGS} replace /> },
          { path: "operations", element: <Navigate to={ROUTES.KNOWLEDGE_OPS} replace /> },
          { path: "knowledge", element: <Navigate to={ROUTES.KNOWLEDGE_CORPUS} replace /> },
          { path: "ingest", element: <Navigate to={ROUTES.KNOWLEDGE_INGEST} replace /> },
        ],
      },
      {
        path: "ranking",
        element: <Navigate to={ROUTES.RANKING} replace />,
      },
      {
        path: "hub-data",
        element: <Navigate to={ROUTES.PARTIES} replace />,
      },
      {
        path: "customers",
        children: [
          { index: true, element: <CustomersLegacyRedirect /> },
          { path: ":partyId", element: <LegacyRouteParamRedirect base={ROUTES.PARTIES} paramKey="partyId" /> },
        ],
      },
      {
        path: "parties",
        element: workspacePage(<PartiesOutletLayout />),
        children: [
          { index: true, element: workspacePage(<PartiesPage partyRole="customer" />) },
          { path: "identity-review", element: workspacePage(<PartyIdentityReviewRoute />) },
          { path: ":partyId", element: workspacePage(<PartyDetailRoute partyRole="customer" />) },
        ],
      },
      {
        path: "parties/suppliers",
        element: workspacePage(<PartiesOutletLayout />),
        children: [
          { index: true, element: workspacePage(<PartiesPage partyRole="supplier" />) },
          { path: ":partyId", element: workspacePage(<PartyDetailRoute partyRole="supplier" />) },
        ],
      },
      {
        path: "parties/partners",
        element: workspacePage(<PartiesOutletLayout />),
        children: [
          { index: true, element: workspacePage(<PartiesPage partyRole="partner" />) },
          { path: ":partyId", element: workspacePage(<PartyDetailRoute partyRole="partner" />) },
        ],
      },
      {
        path: "dashboard",
        element: workspacePage(<IntakeOutletLayout />),
        children: [{ index: true, element: workspacePage(<IntakeDashboardPage />) }],
      },
      {
        path: "platform/search",
        element: workspacePage(<WorkspaceOutletLayout />),
        children: [{ index: true, element: workspacePage(<PlatformSearchPage />) }],
      },
      {
        path: "drafts",
        element: workspacePage(<IntakeOutletLayout />),
        children: [
          { index: true, element: workspacePage(<IntakeDraftsPage />) },
          { path: ":recordId", element: workspacePage(<IntakeDraftEntryPage />) },
        ],
      },
      {
        path: "draft",
        children: [
          { index: true, element: <Navigate to={ROUTES.INTAKE_DRAFTS} replace /> },
          { path: ":recordId", element: <LegacyRouteParamRedirect base={ROUTES.INTAKE_DRAFTS} paramKey="recordId" /> },
        ],
      },
      {
        path: "policy",
        element: <Navigate to={ROUTES.GOVERNANCE_CONTEXT_ENABLEMENT} replace />,
      },
      {
        path: "user-guide",
        children: [
          { index: true, element: workspacePage(<DocsHubPage />) },
          { path: "guide", element: workspacePage(<UserGuidePage />) },
          { path: "tech-spec", element: workspacePage(<TechSpecPage />) },
          { path: "admin-checklist", element: workspacePage(<AdminChecklistPage />) },
        ],
      },
      {
        path: "digest",
        element: workspacePage(<IntakeOutletLayout />),
        children: [{ index: true, element: workspacePage(<IntakeOperationsDigestPage />) }],
      },
      {
        path: "records",
        element: workspacePage(<IntakeOutletLayout />),
        children: [
          { index: true, element: workspacePage(<IntakeRecordsPage />) },
          { path: ":recordId", element: workspacePage(<IntakeRecordDetailPage />) },
        ],
      },
      {
        path: "conversations",
        element: workspacePage(<IntakeOutletLayout />),
        children: [
          { index: true, element: workspacePage(<ConsultationPage />) },
          { path: "channel-scope", element: workspacePage(<WorkScopePage />) },
        ],
      },
      {
        path: "consultation",
        element: <LegacyRouteRedirect to={ROUTES.CONVERSATIONS} />,
      },
      {
        path: "consultation/work-scope",
        element: <LegacyRouteRedirect to={ROUTES.CONVERSATIONS_CHANNEL_SCOPE} />,
      },
      {
        path: "ledger",
        element: workspacePage(<IntakeOutletLayout />),
        children: [{ index: true, element: workspacePage(<IntakeLedgerPage />) }],
      },
      {
        path: "mailbox",
        element: workspacePage(<IntakeOutletLayout />),
        children: [{ index: true, element: workspacePage(<GovernanceIntegrationsPage />) }],
      },
      {
        path: "notification",
        element: <Navigate to={ROUTES.INTAKE_DIGEST} replace />,
      },
      {
        path: "projects",
        element: workspacePage(<WorkOutletLayout />),
        children: [
          { index: true, element: workspacePage(<WorkProjectsPage />) },
          { path: "new", element: workspacePage(<WorkProjectCreateRoute />) },
          { path: ":projectId", element: workspacePage(<WorkProjectDetailRoute />) },
        ],
      },
      {
        path: "tasks",
        element: workspacePage(<WorkOutletLayout />),
        children: [
          { index: true, element: workspacePage(<WorkTasksPage />) },
          { path: ":taskId", element: workspacePage(<WorkTaskDetailRoute />) },
          { path: "new", element: <Navigate to={`${ROUTES.WORK_TASKS}?mode=create`} replace /> },
        ],
      },
      {
        path: "custom-view",
        element: workspacePage(<WorkOutletLayout />),
        children: [{ index: true, element: workspacePage(<WorkCustomViewPage />) }],
      },
      {
        path: "operate",
        element: workspacePage(<WorkOutletLayout />),
        children: [{ path: "analytics", element: workspacePage(<WorkAnalyticsPage />) }],
      },
      {
        path: "strategy",
        element: workspacePage(<StrategyOutletLayout />),
        children: [
          { index: true, element: workspacePage(<StrategyPage surface="overview" />) },
          { path: "goals", element: workspacePage(<StrategyPage surface="goals" />) },
          { path: "goals/:goalId", element: workspacePage(<StrategyPage surface="goals" />) },
          { path: "alignment", element: workspacePage(<StrategyPage surface="alignment" />) },
          { path: "reviews", element: workspacePage(<StrategyPage surface="reviews" />) },
        ],
      },
      {
        path: "settings/me",
        element: workspacePage(<WorkspaceOutletLayout />),
        children: [
          { index: true, element: <Navigate to={ROUTES.USER_ACCOUNT_SETTINGS} replace /> },
          { path: "profile", element: workspacePage(<UserWorkspaceProfilePage />) },
          { path: "mail", element: workspacePage(<UserWorkspaceMailPage />) },
          { path: "ai-defaults", element: workspacePage(<UserWorkspaceAiDefaultsPage />) },
          { path: "zalo", element: workspacePage(<UserWorkspaceZaloPage />) },
          { path: "password", element: <Navigate to={ROUTES.USER_ACCOUNT_SETTINGS} replace /> },
          { path: "mail/oauth-close", element: workspacePage(<UserWorkspaceMailPage />) },
        ],
      },
      {
        path: "prompt-templates",
        element: workspacePage(<GovernanceOutletLayout />),
        children: [{ index: true, element: workspacePage(<GovernancePromptTemplatesPage />) }],
      },
      {
        path: "triage-templates",
        element: workspacePage(<GovernanceOutletLayout />),
        children: [{ index: true, element: workspacePage(<GovernanceTriageTemplatesRoute />) }],
      },
      {
        path: "thread-digest-templates",
        element: workspacePage(<GovernanceOutletLayout />),
        children: [{ index: true, element: workspacePage(<GovernanceThreadDigestTemplatesRoute />) }],
      },
      {
        path: "request-info-templates",
        element: workspacePage(<GovernanceOutletLayout />),
        children: [{ index: true, element: workspacePage(<GovernanceRequestInfoTemplatesRoute />) }],
      },
      {
        path: "rules",
        element: workspacePage(<GovernanceOutletLayout />),
        children: [
          { index: true, element: workspacePage(<GovernanceRulesPage />) },
          { path: "llm-privacy", element: workspacePage(<GovernanceLlmPrivacyPage />) },
          { path: "template-routing-rules", element: workspacePage(<GovernanceTemplateRoutingRulesPage />) },
        ],
      },
      {
        path: "ai-audit",
        element: workspacePage(<GovernanceOutletLayout />),
        children: [
          { index: true, element: workspacePage(<GovernanceAiAuditPage />) },
          { path: ":auditId", element: workspacePage(<GovernanceAiAuditEntryPage />) },
        ],
      },
      {
        path: "llm-privacy",
        element: workspacePage(<GovernanceOutletLayout />),
        children: [{ index: true, element: workspacePage(<GovernanceLlmPrivacyPage />) }],
      },
      {
        path: "contracts",
        element: workspacePage(<ContractsOutletLayout />),
        children: [
          { index: true, element: workspacePage(<ContractsAgreementsPage />) },
          { path: "templates", element: workspacePage(<ContractsTemplatesPage />) },
          { path: "templates/:templateUuid", element: workspacePage(<ContractsTemplateDetailRoute />) },
          { path: ":agreementId", element: workspacePage(<ContractsAgreementDetailRoute />) },
        ],
      },
      {
        path: "finance",
        element: workspacePage(<FinanceOutletLayout />),
        children: [
          { index: true, element: <Navigate to={ROUTES.FINANCE_INVOICES} replace /> },
          { path: "invoices", element: workspacePage(<FinanceInvoicesPage />) },
          { path: "invoices/:invoiceId", element: workspacePage(<FinanceInvoiceDetailRoute />) },
          { path: "payables", element: workspacePage(<FinancePayablesPage />) },
          { path: "payables/:payableId", element: workspacePage(<FinancePayableDetailRoute />) },
          { path: "billing", element: workspacePage(<FinancePaymentDossiersPage />) },
          { path: "billing/:dossierTypeId", element: workspacePage(<FinancePaymentDossierDetailRoute />) },
          { path: "payment-dossiers", element: <Navigate to={ROUTES.FINANCE_BILLING} replace /> },
          { path: "receiving-identity", element: <Navigate to={ROUTES.FINANCE_BILLING} replace /> },
          { path: "billing-profile", element: <Navigate to={ROUTES.FINANCE_BILLING} replace /> },
          { path: "bank", element: workspacePage(<FinanceBankRoute />) },
          { path: "bank/transactions", element: workspacePage(<FinanceBankTransactionsRoute />) },
          { path: "bank/:bankAccountId", element: workspacePage(<FinanceBankAccountDetailRoute />) },
          { path: "statements", element: <Navigate to={ROUTES.FINANCE_BANK} replace /> },
          { path: "reconciliation", element: workspacePage(<FinanceReconciliationPage />) },
          { path: "reporting", element: workspacePage(<FinanceReportingRoute />) },
          { path: "reports", element: <Navigate to={ROUTES.FINANCE_REPORTING} replace /> },
          { path: "content-review", element: <Navigate to={ROUTES.KNOWLEDGE_CONTENT_REVIEW} replace /> },
        ],
      },
      {
        path: "hrm",
        element: workspacePage(<HrmOutletLayout />),
        children: [
          { index: true, element: <Navigate to={ROUTES.HRM_EMPLOYEES} replace /> },
          { path: "org/legal-entities", element: workspacePage(<HrmCatalogPage kind="legal-entities" />) },
          { path: "org/company-levels", element: workspacePage(<HrmCatalogPage kind="company-levels" />) },
          { path: "org/business-lines", element: workspacePage(<HrmCatalogPage kind="business-lines" />) },
          { path: "org/employee-groups", element: workspacePage(<HrmCatalogPage kind="employee-groups" />) },
          { path: "org/specialties", element: workspacePage(<HrmCatalogPage kind="specialties" />) },
          { path: "org/title-groups", element: workspacePage(<HrmCatalogPage kind="title-groups" />) },
          { path: "org/job-families", element: workspacePage(<HrmCatalogPage kind="job-families" />) },
          { path: "org/job-groups", element: workspacePage(<HrmCatalogPage kind="job-groups" />) },
          { path: "org/org-levels", element: workspacePage(<HrmCatalogPage kind="org-levels" />) },
          { path: "org/titles", element: workspacePage(<HrmCatalogPage kind="titles" />) },
          { path: "org/jobs", element: workspacePage(<HrmCatalogPage kind="jobs" />) },
          { path: "org/org-units", element: workspacePage(<HrmCatalogPage kind="org-units" />) },
          { path: "org/positions", element: workspacePage(<HrmCatalogPage kind="positions" />) },
          { path: "org/headcounts", element: workspacePage(<HrmCatalogPage kind="headcounts" />) },
          { path: "hr/nationalities", element: workspacePage(<HrmCatalogPage kind="nationalities" />) },
          { path: "hr/ethnicities", element: workspacePage(<HrmCatalogPage kind="ethnicities" />) },
          { path: "hr/banks", element: workspacePage(<HrmCatalogPage kind="banks" />) },
          { path: "hr/education-levels", element: workspacePage(<HrmCatalogPage kind="education-levels" />) },
          { path: "hr/professional-titles", element: workspacePage(<HrmCatalogPage kind="professional-titles" />) },
          { path: "hr/employee-types", element: workspacePage(<HrmCatalogPage kind="employee-types" />) },
          { path: "hr/standard-workdays", element: workspacePage(<HrmCatalogPage kind="standard-workdays" />) },
          { path: "hr/attendance-objects", element: workspacePage(<HrmCatalogPage kind="attendance-objects" />) },
          { path: "hr/attendance-work-types", element: workspacePage(<HrmCatalogPage kind="attendance-work-types" />) },
          { path: "hr/attendance-symbols", element: workspacePage(<HrmCatalogPage kind="attendance-symbols" />) },
          { path: "hr/attendance-holidays", element: workspacePage(<HrmCatalogPage kind="attendance-holidays" />) },
          { path: "hr/attendance-shifts", element: workspacePage(<HrmCatalogPage kind="attendance-shifts" />) },
          { path: "hr/attendance-shift-schedules", element: workspacePage(<HrmCatalogPage kind="attendance-shift-schedules" />) },
          { path: "hr/ot-time-windows", element: workspacePage(<HrmCatalogPage kind="ot-time-windows" />) },
          { path: "hr/ot-reasons", element: workspacePage(<HrmCatalogPage kind="ot-reasons" />) },
          { path: "hr/ot-plans", element: workspacePage(<HrmCatalogPage kind="ot-plans" />) },
          { path: "hr/ot-plan-lines", element: workspacePage(<HrmCatalogPage kind="ot-plan-lines" />) },
          { path: "hr/collective-business-trips", element: workspacePage(<HrmCatalogPage kind="collective-business-trips" />) },
          { path: "hr/collective-business-trip-lines", element: workspacePage(<HrmCatalogPage kind="collective-business-trip-lines" />) },
          { path: "hr/title-policies", element: workspacePage(<HrmCatalogPage kind="title-policies" />) },
          { path: "hr/birthday-plans", element: workspacePage(<HrmCatalogPage kind="birthday-plans" />) },
          { path: "hr/key-birthday-plans", element: workspacePage(<HrmCatalogPage kind="key-birthday-plans" />) },
          { path: "hr/religions", element: workspacePage(<HrmCatalogPage kind="religions" />) },
          { path: "hr/marital-statuses", element: workspacePage(<HrmCatalogPage kind="marital-statuses" />) },
          { path: "hr/healthcare-places", element: workspacePage(<HrmCatalogPage kind="healthcare-places" />) },
          { path: "hr/training-schools", element: workspacePage(<HrmCatalogPage kind="training-schools" />) },
          { path: "hr/study-majors", element: workspacePage(<HrmCatalogPage kind="study-majors" />) },
          { path: "hr/practice-forms", element: workspacePage(<HrmCatalogPage kind="practice-forms" />) },
          { path: "hr/practice-durations", element: workspacePage(<HrmCatalogPage kind="practice-durations" />) },
          { path: "hr/doctor-competencies", element: workspacePage(<HrmCatalogPage kind="doctor-competencies" />) },
          { path: "hr/occupations", element: workspacePage(<HrmCatalogPage kind="occupations" />) },
          { path: "hr/hospitals", element: workspacePage(<HrmCatalogPage kind="hospitals" />) },
          { path: "hr/health-statuses", element: workspacePage(<HrmCatalogPage kind="health-statuses" />) },
          { path: "hr/health-exam-classifications", element: workspacePage(<HrmCatalogPage kind="health-exam-classifications" />) },
          { path: "hr/occupational-diseases", element: workspacePage(<HrmCatalogPage kind="occupational-diseases" />) },
          { path: "hr/foreign-language-levels", element: workspacePage(<HrmCatalogPage kind="foreign-language-levels" />) },
          { path: "hr/computer-skill-levels", element: workspacePage(<HrmCatalogPage kind="computer-skill-levels" />) },
          { path: "hr/work-locations", element: workspacePage(<HrmCatalogPage kind="work-locations" />) },
          { path: "hr/business-trip-reasons", element: workspacePage(<HrmCatalogPage kind="business-trip-reasons" />) },
          { path: "hr/contract-groups", element: workspacePage(<HrmCatalogPage kind="contract-groups" />) },
          { path: "hr/contract-types", element: workspacePage(<HrmCatalogPage kind="contract-types" />) },
          { path: "hr/constitution-bases", element: workspacePage(<HrmCatalogPage kind="constitution-bases" />) },
          { path: "hr/decision-bases", element: workspacePage(<HrmCatalogPage kind="decision-bases" />) },
          { path: "hr/decision-types", element: workspacePage(<HrmCatalogPage kind="decision-types" />) },
          {
            path: "hr/establishment-reasons",
            element: workspacePage(<HrmCatalogPage kind="establishment-reasons" />),
          },
          {
            path: "hr/establishment-decisions",
            element: workspacePage(<HrmCatalogPage kind="establishment-decisions" />),
          },
          { path: "hr/directive-bases", element: workspacePage(<HrmCatalogPage kind="directive-bases" />) },
          { path: "hr/discipline-forms", element: workspacePage(<HrmCatalogPage kind="discipline-forms" />) },
          { path: "hr/resignation-reasons", element: workspacePage(<HrmCatalogPage kind="resignation-reasons" />) },
          { path: "hr/reward-forms", element: workspacePage(<HrmCatalogPage kind="reward-forms" />) },
          { path: "hr/training-classifications", element: workspacePage(<HrmCatalogPage kind="training-classifications" />) },
          { path: "hr/training-systems", element: workspacePage(<HrmCatalogPage kind="training-systems" />) },
          { path: "hr/clinical-specialties", element: workspacePage(<HrmCatalogPage kind="clinical-specialties" />) },
          { path: "hr/specialty-disease-classifications", element: workspacePage(<HrmCatalogPage kind="specialty-disease-classifications" />) },
          { path: "hr/salary-change-reasons", element: workspacePage(<HrmCatalogPage kind="salary-change-reasons" />) },
          { path: "hr/position-change-reasons", element: workspacePage(<HrmCatalogPage kind="position-change-reasons" />) },
          { path: "hr/technical-specialties", element: workspacePage(<HrmCatalogPage kind="technical-specialties" />) },
          {
            path: "hr/dmkt-registration-campaigns",
            element: workspacePage(<HrmCatalogPage kind="dmkt-registration-campaigns" />),
          },
          { path: "hr/certificates", element: workspacePage(<HrmCatalogPage kind="certificates" />) },
          { path: "hr/diplomas", element: workspacePage(<HrmCatalogPage kind="diplomas" />) },
          { path: "hr/health-records", element: workspacePage(<HrmCatalogPage kind="health-records" />) },
          { path: "hr/procedure-dossiers", element: workspacePage(<HrmCatalogPage kind="procedure-dossiers" />) },
          { path: "hr/evaluation-records", element: workspacePage(<HrmCatalogPage kind="evaluation-records" />) },
          { path: "hr/onboarding-records", element: workspacePage(<HrmCatalogPage kind="onboarding-records" />) },
          { path: "hr/job-description-records", element: workspacePage(<HrmCatalogPage kind="job-description-records" />) },
          { path: "hr/training-histories", element: workspacePage(<HrmCatalogPage kind="training-histories" />) },
          { path: "hr/contracts", element: workspacePage(<HrmCatalogPage kind="contracts" />) },
          { path: "hr/work-histories", element: workspacePage(<HrmCatalogPage kind="work-histories" />) },
          { path: "hr/salary-decisions", element: workspacePage(<HrmCatalogPage kind="salary-decisions" />) },
          { path: "hr/family-relations", element: workspacePage(<HrmCatalogPage kind="family-relations" />) },
          { path: "hr/leave-records", element: workspacePage(<HrmCatalogPage kind="leave-records" />) },
          { path: "hr/discipline-histories", element: workspacePage(<HrmCatalogPage kind="discipline-histories" />) },
          { path: "hr/reward-histories", element: workspacePage(<HrmCatalogPage kind="reward-histories" />) },
          { path: "hr/business-trips", element: workspacePage(<HrmCatalogPage kind="business-trips" />) },
          { path: "hr/maternity-histories", element: workspacePage(<HrmCatalogPage kind="maternity-histories" />) },
          { path: "hr/concurrent-roles", element: workspacePage(<HrmCatalogPage kind="concurrent-roles" />) },
          { path: "hr/social-insurance-histories", element: workspacePage(<HrmCatalogPage kind="social-insurance-histories" />) },
          { path: "hr/end-concurrent-roles", element: workspacePage(<HrmCatalogPage kind="end-concurrent-roles" />) },
          { path: "hr/late-early-regimes", element: workspacePage(<HrmCatalogPage kind="late-early-regimes" />) },
          { path: "hr/supplemental-leave-grants", element: workspacePage(<HrmCatalogPage kind="supplemental-leave-grants" />) },
          { path: "hr/leave-year-balances", element: workspacePage(<HrmCatalogPage kind="leave-year-balances" />) },
          { path: "hr/compensatory-leave-grants", element: workspacePage(<HrmCatalogPage kind="compensatory-leave-grants" />) },
          { path: "hr/practice-certificates", element: workspacePage(<HrmCatalogPage kind="practice-certificates" />) },
          { path: "hr/periodic-health-exams", element: workspacePage(<HrmCatalogPage kind="periodic-health-exams" />) },
          { path: "hr/hepatitis-b-vaccinations", element: workspacePage(<HrmCatalogPage kind="hepatitis-b-vaccinations" />) },
          { path: "hr/cooperations", element: workspacePage(<HrmCatalogPage kind="cooperations" />) },
          { path: "hr/work-accidents", element: workspacePage(<HrmCatalogPage kind="work-accidents" />) },
          { path: "hr/employee-files", element: workspacePage(<HrmCatalogPage kind="employee-files" />) },
          { path: "hr/occupational-disease-cases", element: workspacePage(<HrmCatalogPage kind="occupational-disease-cases" />) },
          { path: "hr/practice-mentorships", element: workspacePage(<HrmCatalogPage kind="practice-mentorships" />) },
          {
            path: "hr/practice-guidance-active",
            element: workspacePage(<HrmCatalogPage kind="practice-guidance-active" />),
          },
          {
            path: "hr/practice-mentor-eligibility",
            element: workspacePage(<HrmCatalogPage kind="practice-mentor-eligibility" />),
          },
          { path: "hr/salary-proposals", element: workspacePage(<HrmCatalogPage kind="salary-proposals" />) },
          { path: "hr/technical-competencies", element: workspacePage(<HrmCatalogPage kind="technical-competencies" />) },
          { path: "hr/leave-carryover-extensions", element: workspacePage(<HrmCatalogPage kind="leave-carryover-extensions" />) },
          { path: "hr/regional-minimum-wages", element: workspacePage(<HrmCatalogPage kind="regional-minimum-wages" />) },
          { path: "hr/si-participation-rates", element: workspacePage(<HrmCatalogPage kind="si-participation-rates" />) },
          { path: "hr/attendance-periods", element: workspacePage(<HrmCatalogPage kind="attendance-periods" />) },
          { path: "hr/payroll-periods", element: workspacePage(<HrmCatalogPage kind="payroll-periods" />) },
          { path: "hr/payroll-item-groups", element: workspacePage(<HrmCatalogPage kind="payroll-item-groups" />) },
          { path: "hr/payroll-items", element: workspacePage(<HrmCatalogPage kind="payroll-items" />) },
          { path: "hr/payroll-item-formulas", element: workspacePage(<HrmCatalogPage kind="payroll-item-formulas" />) },
          {
            path: "hr/si-monthly-contributions",
            element: workspacePage(<HrmCatalogPage kind="si-monthly-contributions" />),
          },
          {
            path: "hr/external-income-individuals",
            element: workspacePage(<HrmCatalogPage kind="external-income-individuals" />),
          },
          {
            path: "hr/external-income-blocks",
            element: workspacePage(<HrmCatalogPage kind="external-income-blocks" />),
          },
          {
            path: "hr/salary-plan-entries",
            element: workspacePage(<HrmCatalogPage kind="salary-plan-entries" />),
          },
          {
            path: "hr/classified-revenues",
            element: workspacePage(<HrmCatalogPage kind="classified-revenues" />),
          },
          {
            path: "hr/ky1-benefit-rates",
            element: workspacePage(<HrmCatalogPage kind="ky1-benefit-rates" />),
          },
          {
            path: "hr/hospital-monthly-revenues",
            element: workspacePage(<HrmCatalogPage kind="hospital-monthly-revenues" />),
          },
          {
            path: "hr/duty-conversion-entries",
            element: workspacePage(<HrmCatalogPage kind="duty-conversion-entries" />),
          },
          {
            path: "hr/timesheet-daily-entries",
            element: workspacePage(<HrmCatalogPage kind="timesheet-daily-entries" />),
          },
          {
            path: "hr/late-early-violations",
            element: workspacePage(<HrmCatalogPage kind="late-early-violations" />),
          },
          {
            path: "hr/duty-fund-ledgers",
            element: workspacePage(<HrmCatalogPage kind="duty-fund-ledgers" />),
          },
          {
            path: "hr/comp-leave-fund-ledgers",
            element: workspacePage(<HrmCatalogPage kind="comp-leave-fund-ledgers" />),
          },
          {
            path: "hr/fund-opening-balances",
            element: workspacePage(<HrmCatalogPage kind="fund-opening-balances" />),
          },
          { path: "hr/payroll-snapshots", element: workspacePage(<HrmCatalogPage kind="payroll-snapshots" />) },
          { path: "portal/leave-requests", element: workspacePage(<HrmCatalogPage kind="leave-requests" />) },
          {
            path: "portal/leave-request-approval-steps",
            element: workspacePage(<HrmCatalogPage kind="leave-request-approval-steps" />),
          },
          { path: "portal/business-trip-requests", element: workspacePage(<HrmCatalogPage kind="business-trip-requests" />) },
          { path: "portal/ot-requests", element: workspacePage(<HrmCatalogPage kind="ot-requests" />) },
          {
            path: "portal/ot-request-approval-steps",
            element: workspacePage(<HrmCatalogPage kind="ot-request-approval-steps" />),
          },
          {
            path: "portal/ot-request-edit-events",
            element: workspacePage(<HrmCatalogPage kind="ot-request-edit-events" />),
          },
          {
            path: "portal/request-attachments",
            element: workspacePage(<HrmCatalogPage kind="request-attachments" />),
          },
          { path: "portal/leave-balances", element: workspacePage(<HrmCatalogPage kind="leave-balances" />) },
          {
            path: "portal/timesheet-daily-entries",
            element: workspacePage(<HrmCatalogPage kind="portal-timesheet-daily-entries" />),
          },
          {
            path: "portal/attendance-explanation-requests",
            element: workspacePage(<HrmCatalogPage kind="attendance-explanation-requests" />),
          },
          {
            path: "portal/dmkt-registration-requests",
            element: workspacePage(<HrmCatalogPage kind="dmkt-registration-requests" />),
          },
          {
            path: "portal/dmkt-registration-lines",
            element: workspacePage(<HrmCatalogPage kind="dmkt-registration-lines" />),
          },
          {
            path: "portal/dmkt-registration-approval-steps",
            element: workspacePage(<HrmCatalogPage kind="dmkt-registration-approval-steps" />),
          },
          {
            path: "portal/dmkt-registration-edit-events",
            element: workspacePage(<HrmCatalogPage kind="dmkt-registration-edit-events" />),
          },
          { path: "portal/resignation-requests", element: workspacePage(<HrmCatalogPage kind="resignation-requests" />) },
          { path: "hr/alerts", element: workspacePage(<HrmCatalogPage kind="alerts" />) },
          { path: "hr/form-layouts", element: workspacePage(<HrmCatalogPage kind="form-layouts" />) },
          { path: "hr/report-layouts", element: workspacePage(<HrmCatalogPage kind="report-layouts" />) },
          { path: "hr/abr-phase", element: workspacePage(<HrmCatalogPage kind="abr-phase" />) },
          { path: "integrations/ad-ldap", element: workspacePage(<HrmCatalogPage kind="ad-ldap" />) },
          { path: "integrations/webhooks", element: workspacePage(<HrmCatalogPage kind="webhooks" />) },
          { path: "integrations/his-revenue", element: workspacePage(<HrmCatalogPage kind="his-revenue" />) },
          { path: "integrations/nfr-security", element: workspacePage(<HrmCatalogPage kind="nfr-security" />) },
          { path: "hr/employees", element: workspacePage(<HrmCatalogPage kind="employees" />) },
        ],
      },
      {
        path: "content",
        children: [
          { index: true, element: <Navigate to={ROUTES.KNOWLEDGE_CONTENT} replace /> },
          { path: "capture", element: <Navigate to={ROUTES.KNOWLEDGE_CONTENT_CAPTURE} replace /> },
          { path: "review", element: <Navigate to={ROUTES.KNOWLEDGE_CONTENT_REVIEW} replace /> },
          { path: "library", element: <Navigate to={ROUTES.KNOWLEDGE_CONTENT_LIBRARY} replace /> },
          { path: "library/:itemId", element: <ContentLibraryItemLegacyRedirect /> },
          { path: "storage", element: <Navigate to={ROUTES.KNOWLEDGE_CONTENT_STORAGE} replace /> },
        ],
      },
      {
        path: "admin/llm-runtime",
        element: <Navigate to={ROUTES.AI_WORKFLOW_POLICY} replace />,
      },
      {
        path: "llm-runtime",
        element: <Navigate to={ROUTES.AI_WORKFLOW_POLICY} replace />,
      },
      {
        path: "admin",
        element: workspacePage(<GovernanceOutletLayout />),
        children: [
          { index: true, element: workspacePage(<GovernanceAdministrationPage />) },
          { path: "operational", element: workspacePage(<GovernanceOperationalPage />) },
          { path: "integrations", element: workspacePage(<GovernanceIntegrationsPage />) },
          { path: "sync-diagnostics", element: workspacePage(<GovernanceSyncDiagnosticsPage />) },
          { path: "authz", element: <Navigate to={ROUTES.ADMIN_AUTHZ_ROLES} replace /> },
          { path: "authz/roles", element: <Navigate to={ROUTES.ADMIN_AUTHZ_ROLES} replace /> },
          { path: "authz/users", element: <Navigate to={ROUTES.ADMIN_AUTHZ_USERS} replace /> },
          { path: "rules", element: workspacePage(<GovernanceRulesPage />) },
          { path: "template-routing-rules", element: workspacePage(<GovernanceTemplateRoutingRulesPage />) },
          { path: "ledger", element: workspacePage(<IntakeLedgerPage />) },
          { path: "ledger/:entryId", element: workspacePage(<IntakeLedgerEntryPage />) },
        ],
      },
      {
        path: "governance/knowledge",
        element: workspacePage(<DataGovernanceOutletLayout />),
        children: [
          { index: true, element: <Navigate to={ROUTES.GOVERNANCE_CONTEXT_CATALOG} replace /> },
          { path: "catalog", element: workspacePage(<CatalogPage />) },
          { path: "enablement", element: workspacePage(<PolicyPage />) },
          { path: "drift", element: workspacePage(<DriftPage />) },
          { path: "ops", element: workspacePage(<ProjectionPage />) },
          { path: "policies", element: <Navigate to={ROUTES.GOVERNANCE_CONTEXT_ENABLEMENT} replace /> },
          { path: "health", element: <Navigate to={ROUTES.GOVERNANCE_CONTEXT_DRIFT} replace /> },
          { path: "operations", element: <Navigate to={ROUTES.GOVERNANCE_CONTEXT_OPS} replace /> },
        ],
      },
      {
        path: "data-governance",
        children: [
          { index: true, element: <Navigate to={ROUTES.GOVERNANCE_CONTEXT_CATALOG} replace /> },
          { path: "catalog", element: <Navigate to={ROUTES.GOVERNANCE_CONTEXT_CATALOG} replace /> },
          { path: "policy", element: <Navigate to={ROUTES.GOVERNANCE_CONTEXT_ENABLEMENT} replace /> },
          { path: "drift", element: <Navigate to={ROUTES.GOVERNANCE_CONTEXT_DRIFT} replace /> },
          { path: "projection", element: <Navigate to={ROUTES.GOVERNANCE_CONTEXT_OPS} replace /> },
        ],
      },
      {
        path: "intake",
        children: legacyIntakeRouteChildren,
      },
      {
        path: "intake-hub",
        children: legacyIntakeRouteChildren,
      },
      {
        path: "iam",
        element: workspacePage(<IamOutletLayout />),
        children: [
          { index: true, element: <Navigate to="/iam/tenants" replace /> },
          { path: "tenants", element: workspacePage(<IamTenantsPage />) },
          {
            path: "tenants/:tenantId/roles/:roleRecordId",
            element: workspacePage(<IamRoleDetailPage />),
          },
          { path: "tenants/:tenantId", element: workspacePage(<IamTenantDetailPage />) },
          { path: "services", element: workspacePage(<IamServicesPage />) },
          { path: "service-items", element: workspacePage(<IamServiceItemsPage />) },
          { path: "sync", element: workspacePage(<IamSyncPage />) },
          { path: "audit", element: workspacePage(<IamAuditPage />) },
          { path: "simulator", element: workspacePage(<IamSimulatorPage />) },
          {
            path: "roles",
            children: [
              { index: true, element: workspacePage(<IamRolesPage />) },
              { path: ":roleRecordId", element: workspacePage(<IamRoleDetailPage />) },
            ],
          },
          { path: "users", element: workspacePage(<IamUsersPage />) },
          { path: "users/:userId", element: workspacePage(<IamUserDetailPage />) },
        ],
      },
      { path: "users", element: <Navigate to="/iam/users" replace /> },
      {
        path: "analytics",
        element: workspacePage(<ObservabilityOutletLayout />),
        children: [{ index: true, element: workspacePage(<AnalyticsPage />) }],
      },
      {
        path: "analytics/dashboard",
        element: <Navigate to="/analytics" replace />,
      },
      {
        path: "database",
        element: (
          <Suspense fallback={<PageLoading />}>
            <DatabasePage />
          </Suspense>
        ),
      },
      {
        path: "database/management",
        element: (
          <Suspense fallback={<PageLoading />}>
            <DatabaseManagementPage />
          </Suspense>
        ),
      },
      {
        path: "settings",
        element: <Navigate to={ROUTES.USER_ACCOUNT_SETTINGS} replace />,
      },
      {
        path: "monitoring",
        element: workspacePage(<ObservabilityOutletLayout />),
        children: [
          { index: true, element: workspacePage(<MonitoringPage />) },
          { path: "system", element: <Navigate to="/monitoring" replace /> },
        ],
      },
      {
        path: "services",
        element: (
          <Suspense fallback={<PageLoading />}>
            <ServicesPage />
          </Suspense>
        ),
      },
      {
        path: "deployment",
        element: (
          <Suspense fallback={<PageLoading />}>
            <DeploymentPage />
          </Suspense>
        ),
      },
      {
        path: "performance",
        element: (
          <Suspense fallback={<PageLoading />}>
            <PerformancePage />
          </Suspense>
        ),
      },
      {
        path: "*",
        element: withDocumentTitle(<NotFoundPage />),
      },
    ],
  },
  {
    path: "embed/chat",
    element: (
      <RouteErrorBoundary>
        <Suspense fallback={<PageLoading />}>
          <EmbedChatPage />
        </Suspense>
      </RouteErrorBoundary>
    ),
  },
];

export const browserRouter = createBrowserRouter(appRoutes);

export { ROUTE_PREFETCH_LOADERS } from "../lib/constants/routes/route-prefetch";
